import { buildPlot, generateColors, NeedlePlot } from './NeedlePlot';
import { Chip } from '@lifeomic/chroma-react/components/Chip';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles, useTheme } from '@lifeomic/chroma-react/styles';
import { PlotContainer } from '@lifeomic/phc-web-toolkit/src/components/Plot/PlotContainer';
import * as React from 'react';
import KnowledgeService from '@lifeomic/phc-web-toolkit/src/services/KnowledgeService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { getVariantType } from './NeedlePlot/utils';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { getAminoAcidField } from '../../utils';

const knowledgeService = new KnowledgeService();

const messages = defineMessages({
  chipSomatic: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.singleShortVariantNeedlePlot.chipSomatic',
    defaultMessage: '{gene} variants in TCGA all tumor samples',
  },
  chipGermline: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.singleShortVariantNeedlePlot.chipGermline',
    defaultMessage: '{gene} {aaChange} {class}',
  },
  primaryName: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.singleShortVariantNeedlePlot.primaaryName',
    defaultMessage: '{aaChange} {class} variant from sample',
  },
  xLabel: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.singleShortVariantNeedlePlot.xLabel',
    defaultMessage: 'Amino Acid Position',
  },
  yLabel: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.singleShortVariantNeedlePlot.yLabel',
    defaultMessage: '# of Mutations',
  },
  tcgaHover: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.singleShortVariantNeedlePlot.tcgaHover',
    defaultMessage: '{aaChange}<br />{cmcTcgaHistogram}',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    height: theme.pxToRem(475),
  },
  plot: {
    width: '100%',
  },
  chip: {
    position: 'absolute',
    top: 0,
    left: theme.spacing(0.5),
    zIndex: 100,
  },
}));

export const SingleShortVariantNeedlePlot = ({
  transcripts,
  transcriptId,
  selectedSequenceType,
}) => {
  const classes = useStyles({});

  const { palette } = useTheme();
  const { formatMessage } = useIntl();
  const accountId = useActiveAccountId();
  const aminoAcidField = getAminoAcidField(accountId);

  const [mutationData, setMutationData] = React.useState(null);
  const [getData, getDatasState] = useService(knowledgeService.getpfamDataSets);
  const [getVariants, getVariantsState] = useService(
    knowledgeService.getVariants,
  );

  const isSomatic = selectedSequenceType === 'somatic';

  React.useEffect(() => {
    if (!transcriptId) {
      return;
    }

    getData(transcriptId);
    if (isSomatic) {
      getVariants(transcriptId);
    }
  }, [getVariants, getData, transcriptId, isSomatic]);

  React.useEffect(() => {
    if (!getDatasState.response && isSomatic && !getVariantsState.response) {
      return;
    }

    const items = getDatasState?.response?.data?.items || [];

    const colorOptions = generateColors({ palette });

    const variantTranscripts = getVariantsState?.response?.data?.items || [];

    if (isSomatic) {
      setMutationData(
        buildPlot({
          formatMessage,
          transcripts: variantTranscripts.map(v => ({
            aminoAcidChange: v.aaChange,
            frequency: v.cmcTcgaCount,
            class: v.effectGroup,
          })),
          items,
          colorOptions,
          primaryTranscript: {
            ...transcripts?.[0],
            name: formatMessage(messages.primaryName, {
              gene: transcripts?.[0].gene,
              aaChange: transcripts?.[0][aminoAcidField],
              class: getVariantType(
                transcripts?.[0].class,
              ).legend.toLowerCase(),
            }),
          },
        }),
      );
    } else {
      setMutationData(
        buildPlot({ formatMessage, transcripts, items, colorOptions }),
      );
    }
  }, [
    aminoAcidField,
    formatMessage,
    transcripts,
    getDatasState,
    getDatasState.response,
    getVariantsState.response,
    palette,
    isSomatic,
  ]);

  return (
    <PlotContainer
      className={classes.root}
      isLoading={getDatasState.isFetching || getVariantsState.isFetching}
    >
      {mutationData && (
        <>
          {transcripts?.[0] && (
            <Chip
              className={classes.chip}
              label={formatMessage(
                isSomatic ? messages.chipSomatic : messages.chipGermline,
                {
                  gene: transcripts[0].gene,
                  aaChange: transcripts[0][aminoAcidField],
                  class: transcripts[0].class,
                },
              )}
            />
          )}
          <NeedlePlot
            className={classes.plot}
            config={{
              displaylogo: false,
              modeBarButtonsToRemove: [
                'sendDataToCloud',
                'select2d',
                'lasso2d',
              ],
            }}
            mutationData={mutationData}
            showNeedlePinLegend={isSomatic}
            showDomainLegend={false}
            showYaxis={true}
            rangeSlider={false}
            domainStyle={{
              displayMinorDomains: false,
            }}
            xLabel={formatMessage(messages.xLabel)}
            yLabel={formatMessage(messages.yLabel)}
          />
        </>
      )}
    </PlotContainer>
  );
};
